import * as React from 'react';
import * as api from '../services/api-client';

/**
 * Assignment state management for the Admin Console.
 * Manages subdomain-to-user assignments.
 * System_Admin + Domain_Admin (scoped to assigned domains).
 * Implements optimistic updates with revert on failure.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Assignment {
  readonly subdomainId: string;
  readonly userId: string;
  readonly email: string;
  readonly displayName?: string;
  readonly mail?: string;
  readonly subdomainLabel: string;
  readonly fqdn: string;
  readonly domainId: string;
  readonly assignedBy: string;
  readonly assignedAt: string;
}

export interface CreateAssignmentInput {
  readonly subdomainId: string;
  readonly userId: string;
  readonly displayName?: string;
  readonly mail?: string;
}

export interface DeleteAssignmentInput {
  readonly subdomainId: string;
  readonly userId: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface AssignmentState {
  readonly assignments: readonly Assignment[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

export const initialAssignmentState: AssignmentState = {
  assignments: [],
  isLoading: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type AssignmentAction =
  | { readonly type: 'ASSIGNMENTS_LOADING' }
  | { readonly type: 'ASSIGNMENTS_LOADED'; readonly payload: readonly Assignment[] }
  | { readonly type: 'ASSIGNMENTS_ERROR'; readonly payload: string }
  | { readonly type: 'ASSIGNMENT_CREATE_OPTIMISTIC'; readonly payload: Assignment }
  | { readonly type: 'ASSIGNMENT_CREATE_CONFIRMED' }
  | { readonly type: 'ASSIGNMENT_CREATE_REVERT'; readonly payload: { readonly previousState: readonly Assignment[] } }
  | { readonly type: 'ASSIGNMENT_DELETE_OPTIMISTIC'; readonly payload: { readonly subdomainId: string; readonly userId: string } }
  | { readonly type: 'ASSIGNMENT_DELETE_CONFIRMED' }
  | { readonly type: 'ASSIGNMENT_DELETE_REVERT'; readonly payload: { readonly previousState: readonly Assignment[] } };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function assignmentReducer(state: AssignmentState, action: AssignmentAction): AssignmentState {
  switch (action.type) {
    case 'ASSIGNMENTS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'ASSIGNMENTS_LOADED':
      return { assignments: action.payload, isLoading: false, error: null };
    case 'ASSIGNMENTS_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    case 'ASSIGNMENT_CREATE_OPTIMISTIC':
      return { ...state, assignments: [...state.assignments, action.payload], error: null };
    case 'ASSIGNMENT_CREATE_CONFIRMED':
      return state;
    case 'ASSIGNMENT_CREATE_REVERT':
      return { ...state, assignments: action.payload.previousState, error: 'Failed to create assignment. Reverted.' };
    case 'ASSIGNMENT_DELETE_OPTIMISTIC':
      return {
        ...state,
        assignments: state.assignments.filter(
          (a) => !(a.subdomainId === action.payload.subdomainId && a.userId === action.payload.userId),
        ),
        error: null,
      };
    case 'ASSIGNMENT_DELETE_CONFIRMED':
      return state;
    case 'ASSIGNMENT_DELETE_REVERT':
      return { ...state, assignments: action.payload.previousState, error: 'Failed to delete assignment. Reverted.' };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface AssignmentContextValue {
  readonly state: AssignmentState;
  readonly loadAssignments: (subdomainId: string) => Promise<void>;
  readonly createAssignment: (input: CreateAssignmentInput) => Promise<void>;
  readonly deleteAssignment: (input: DeleteAssignmentInput) => Promise<void>;
}

const AssignmentContext = React.createContext<AssignmentContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface AssignmentProviderProps {
  readonly children: React.ReactNode;
}

/**
 * AssignmentProvider manages subdomain assignment state with optimistic updates.
 */
export function AssignmentProvider({ children }: AssignmentProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(assignmentReducer, initialAssignmentState);
  const stateRef = React.useRef(state.assignments);
  stateRef.current = state.assignments;

  const loadAssignments = React.useCallback(async (subdomainId: string): Promise<void> => {
    dispatch({ type: 'ASSIGNMENTS_LOADING' });
    try {
      const resp = await api.get<{ assignments?: readonly Assignment[] }>(`/admin/subdomains/${subdomainId}/assignments`); const data = resp.assignments ?? [];
      dispatch({ type: 'ASSIGNMENTS_LOADED', payload: data });
    } catch (err) {
      dispatch({
        type: 'ASSIGNMENTS_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to load assignments',
      });
    }
  }, []);

  const createAssignment = React.useCallback(async (input: CreateAssignmentInput): Promise<void> => {
    const previousState = stateRef.current;
    const optimistic: Assignment = {
      subdomainId: input.subdomainId,
      userId: input.userId,
      email: input.mail ?? 'pending@example.com',
      displayName: input.displayName,
      mail: input.mail,
      subdomainLabel: '',
      fqdn: '',
      domainId: '',
      assignedBy: 'current-user',
      assignedAt: new Date().toISOString(),
    };

    dispatch({ type: 'ASSIGNMENT_CREATE_OPTIMISTIC', payload: optimistic });
    try {
      await api.post(`/admin/subdomains/${input.subdomainId}/assignments`, {
        userId: input.userId,
        displayName: input.displayName,
        mail: input.mail,
      });
      dispatch({ type: 'ASSIGNMENT_CREATE_CONFIRMED' });
      // Reload to get full server data
      await loadAssignments(input.subdomainId);
    } catch (err) {
      dispatch({ type: 'ASSIGNMENT_CREATE_REVERT', payload: { previousState } });
      throw err;
    }
  }, [loadAssignments]);

  const deleteAssignment = React.useCallback(async (input: DeleteAssignmentInput): Promise<void> => {
    const previousState = stateRef.current;

    dispatch({ type: 'ASSIGNMENT_DELETE_OPTIMISTIC', payload: { subdomainId: input.subdomainId, userId: input.userId } });
    try {
      await api.del(`/admin/subdomains/${input.subdomainId}/assignments/${input.userId}`);
      dispatch({ type: 'ASSIGNMENT_DELETE_CONFIRMED' });
    } catch {
      dispatch({ type: 'ASSIGNMENT_DELETE_REVERT', payload: { previousState } });
    }
  }, []);

  const contextValue = React.useMemo(
    (): AssignmentContextValue => ({
      state,
      loadAssignments,
      createAssignment,
      deleteAssignment,
    }),
    [state, loadAssignments, createAssignment, deleteAssignment],
  );

  return React.createElement(AssignmentContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access assignment state and actions.
 * Must be used within an AssignmentProvider.
 */
export function useAssignments(): AssignmentContextValue {
  const context = React.useContext(AssignmentContext);
  if (!context) {
    throw new Error('useAssignments must be used within an AssignmentProvider');
  }
  return context;
}
